/**
 * Profile State Hook
 * 
 * Central state management hook for the Profile component.
 * Handles data fetching, state updates, and business logic.
 */

import { useCallback, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/lib/routes";
import { useReducerWithDevtools } from "@/lib/useReducerWithDevtools";
import { profileReducer, initialProfileState, profileActions } from "@/reducers/profileReducer";

import type { Achievement } from "@/types/state";

export function useProfileState() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [state, dispatch] = useReducerWithDevtools(
    profileReducer,
    initialProfileState,
    'Profile'
  );

  /**
   * Fetch all profile data (gamification and achievements)
   */
  const fetchProfileData = useCallback(async () => {
    if (!user) return;
    
    try {
      // Fetch gamification data
      const { data: gamData, error: gamError } = await supabase
        .from("user_gamification")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (gamError && gamError.code !== "PGRST116") throw gamError;
      dispatch(profileActions.setGamificationData(gamData));

      // Fetch unlocked achievements with details
      const { data: userAchievements, error: userAchError } = await supabase
        .from("user_achievements")
        .select(`
          achievement_id,
          unlocked_at,
          achievements (
            id,
            name,
            description,
            icon,
            tier,
            points_reward,
            requirement_type,
            requirement_value
          )
        `)
        .eq("user_id", user.id)
        .order("unlocked_at", { ascending: false });

      if (userAchError) throw userAchError;

      const unlockedAchievements = (userAchievements || [])
        .map((ua: any) => ({
          ...ua.achievements,
          unlocked_at: ua.unlocked_at,
        }))
        .filter((a): a is Achievement => a !== null);

      dispatch(profileActions.setAchievements(unlockedAchievements));
    } catch (error) {
      console.error("Error fetching profile data:", error);
      toast.error("Failed to load profile data");
    } finally {
      dispatch(profileActions.setLoading(false));
    }
  }, [user]);

  /**
   * Share profile stats
   */
  const handleShareProfile = useCallback(async () => {
    const shareText = `🎮 Level ${state.gamificationData?.current_level || 0} | 🏆 ${state.achievements.length} Achievements | ⭐ ${state.gamificationData?.total_points.toLocaleString() || 0} Points`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Gaming Profile",
          text: shareText,
        });
        toast.success("Profile shared!");
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          navigator.clipboard.writeText(shareText);
          toast.success("Profile stats copied to clipboard!");
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success("Profile stats copied to clipboard!");
    }
  }, [state.gamificationData, state.achievements]);

  /**
   * Navigate to achievements page
   */
  const handleViewAchievements = useCallback(() => {
    navigate(ROUTES.ACHIEVEMENTS);
  }, [navigate]);

  /**
   * Navigate to notification history
   */
  const handleViewNotifications = useCallback(() => {
    navigate(ROUTES.NOTIFICATION_HISTORY);
  }, [navigate]);

  /**
   * Navigate back
   */
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /**
   * Change active tab
   */
  const handleTabChange = useCallback((tab: typeof state.activeTab) => {
    dispatch(profileActions.setActiveTab(tab));
  }, []);

  /**
   * Initialize profile on mount
   */
  useEffect(() => {
    if (user) {
      fetchProfileData();
    }
  }, [user, fetchProfileData]);

  return {
    // State
    ...state,
    user,
    
    // Handlers
    fetchProfileData,
    handleShareProfile,
    handleViewAchievements,
    handleViewNotifications,
    handleGoBack,
    handleTabChange,
  };
}
